import React from 'react';
import { AmpelBadge, Kachel, SignalListe, Abschnitt, FazitBox, LeerHinweis } from 'core/components/ui';
import { formatKurs, formatZahl } from 'core/utils/format';

/** Prozentwert mit Vorzeichen, null-sicher. */
const proz = (v, digits = 1) => (v == null ? '–' : `${v >= 0 ? '+' : ''}${formatZahl(v, digits)} %`);

/** Visuelle 52-Wochen-Spanne mit Markierung des aktuellen Kurses. */
function Spanne52W({ tief, hoch, position, currency }) {
  if (tief == null || hoch == null || position == null) return null;
  const pct = Math.max(0, Math.min(1, position)) * 100;
  return (
    <div className="rounded-xl border border-line bg-paper-soft p-4">
      <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">52-Wochen-Spanne</div>
      <div className="relative h-2 rounded-full bg-paper-sunken">
        <div
          className="absolute -top-1 h-4 w-1 rounded-full bg-ink"
          style={{ left: `calc(${pct}% - 2px)` }}
          aria-hidden="true"
        />
      </div>
      <div className="mt-2 flex justify-between font-mono text-xs text-ink-muted">
        <span>Tief {formatKurs(tief, currency)}</span>
        <span className="text-ink">{formatZahl(pct, 0)} %</span>
        <span>Hoch {formatKurs(hoch, currency)}</span>
      </div>
    </div>
  );
}

export default function RisikoAnalyse({ risiko, currency }) {
  if (!risiko) {
    return <LeerHinweis>Keine Risikodaten vorhanden.</LeerHinweis>;
  }

  return (
    <div>
      {/* Ampel */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-paper-soft p-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">Risikoprofil</div>
          <div className="mt-0.5 font-serif text-2xl font-semibold text-ink">
            {risiko.volatilitaet != null ? `${formatZahl(risiko.volatilitaet, 1)} % Vola p.a.` : 'Risiko'}
          </div>
        </div>
        <AmpelBadge ampel={risiko.ampel} size="lg" />
      </div>

      {/* Kennzahlen-Kacheln */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kachel
          label="Volatilität p.a."
          value={risiko.volatilitaet != null ? `${formatZahl(risiko.volatilitaet, 1)} %` : '–'}
        />
        <Kachel
          label="Max. Drawdown"
          value={risiko.maxDrawdown != null ? `${formatZahl(risiko.maxDrawdown, 1)} %` : '–'}
          tone="loss"
        />
        <Kachel
          label="Rendite 1 J"
          value={proz(risiko.rendite1J)}
          tone={risiko.rendite1J != null && risiko.rendite1J < 0 ? 'loss' : 'gain'}
        />
        <Kachel
          label="Rendite 3 J p.a."
          value={proz(risiko.rendite3J)}
          tone={risiko.rendite3J != null && risiko.rendite3J < 0 ? 'loss' : 'gain'}
        />
      </div>

      <Abschnitt titel="Kursspanne">
        <Spanne52W
          tief={risiko.tief52w}
          hoch={risiko.hoch52w}
          position={risiko.position52w}
          currency={currency}
        />
      </Abschnitt>

      <Abschnitt titel="Bewertung">
        <SignalListe signale={risiko.kennzahlen} />
      </Abschnitt>

      {risiko.betaHinweis && (
        <p className="mb-4 rounded-xl border border-dashed border-line bg-paper-soft p-3 text-xs text-ink-muted">
          ℹ️ {risiko.betaHinweis}
        </p>
      )}

      <FazitBox>{risiko.fazit}</FazitBox>
    </div>
  );
}
